// Pieza sin selector (slot `promotionsSync`): siembra lo apartado y lo reaplica al cambiar la ancla
// o al marcar el wizard algo por revalidar. Bloquea «Continuar» mientras confirma.
import { useEffect } from 'react';
import type { GiftSlotContext } from '@/domains/sales/wizard/steps/BuildSaleStep';
import { Button } from '@/kit';
import { useSalePromotionEvaluation } from '../api/useWizardPromotions';
import { PROMOTIONS_CONFIRMING_REASON, PROMOTIONS_RETRY_REASON } from '../constants';
import { useReapplyOnSaleChange } from '../hooks/useReapplyOnSaleChange';
import { seedSessionPromotions, useSessionPromotionState } from '../store/promotionSelectionStore';

export interface PromotionsSyncProps extends GiftSlotContext {
    sessionId: string;
}

export function PromotionsSync({
    sessionId,
    setSelectionPending,
    ...context
}: PromotionsSyncProps) {
    useEffect(() => {
        seedSessionPromotions(sessionId, context);
    });
    const reapply = useReapplyOnSaleChange({ sessionId, context });
    const evaluation = useSalePromotionEvaluation(sessionId);
    // Un PUT de cualquier pieza en camino (regalo, descuento, ✕) también espera.
    const { pendingChanges = [] } = useSessionPromotionState(sessionId);
    const writing = reapply.isReapplying || pendingChanges.length > 0;
    const reason =
        evaluation.isError || reapply.error
            ? PROMOTIONS_RETRY_REASON
            : evaluation.isLoading || writing
              ? PROMOTIONS_CONFIRMING_REASON
              : undefined;
    useEffect(() => {
        setSelectionPending(reason !== undefined, reason);
    }, [reason, setSelectionPending]);
    useEffect(
        () => () => {
            setSelectionPending(false);
        },
        [setSelectionPending],
    );

    if (!evaluation.isError && !reapply.error) return null;
    return (
        <div role="alert" className="flex flex-wrap items-center gap-2 text-[12px]">
            <span className="text-destructive">
                {reapply.error ?? 'No pudimos cargar las promociones.'}
            </span>
            <Button
                size="sm"
                variant="secondary"
                isLoading={reapply.isReapplying || evaluation.isFetching}
                onClick={() => {
                    if (reapply.error) reapply.retry();
                    else void evaluation.refetch();
                }}
            >
                Reintentar
            </Button>
        </div>
    );
}
